// OrganisationPage — the standing brief (BUILD-BRIEF §6, organisations agent), reached at
// #org?id=N. Back link → mast with statedot → five-cell rail (PEOPLE · DAY · BALL · DEALS ·
// MONITORS) → Next move (a server-composed sentence) → People here → Units → Deals →
// commitments → Dossier (research, §9) → Signals (§8) → Monitors (§8).
//
// ROUND 3b added the tree (BUILD-BRIEF-R3b §2b): a breadcrumb of the whole parent chain instead
// of one bare parent link, a UNITS zone reading GET /organisations/:id/tree, and a PEOPLE cell
// that counts the tree with the direct count under it.
//
// ROUND 4 leaves the page ONE research verb (BUILD-BRIEF-R4 §4). "Find people", its depth toggle
// and "Research now" are gone; `Research` starts a `deep_research` run on this organisation and
// opens its desk. That one run finds the website if the row has none, files the raster cells it
// can cite, and proposes the people it meets — every one of them in Review, with the line that
// names them. Nothing it does writes the record (P1).
//
// MONITORS is counted by GET /api/monitors for this organisation: `fv--unknown` "not collected"
// only while that call has not answered, `fv--nil` when it answered zero. Two different facts.
//
// A ZONE THAT HOLDS NOTHING SAYS SO IN WORDS: `<p class="zone-empty">`, the way the person
// brief does. An EmptyState card is a dashed box the height of a sheet, and four of them down
// one brief turned a quiet organisation into a page of empty boxes. The card is kept for the
// two cases that are NOT an ordinary nothing — an error, and a filter that matches nothing.
import { Fragment, useCallback, useEffect, useState } from 'react';
import EmptyState from '../components/EmptyState.js';
import ConfirmModal from '../components/ConfirmModal.js';
import DossierZone from '../components/DossierZone.js';
import ProfileZone, { TIER_TONE } from '../components/ProfileZone.js';
import NetworkZone from '../components/NetworkZone.js';
import SourceMentionsZone from '../components/SourceMentionsZone.js';
import DocumentsZone from '../components/DocumentsZone.js';
import SignalsZone from '../components/SignalsZone.js';
import { useToast } from '../components/Toast.js';
import { OrgLogo, PersonAvatar } from '../components/Identity.js';
import {
  Att, Fig, Lnk, Mast, PageShell, Rail, RailCell, Ref, Sheet, Tag, Tbl, Zone, ZoneHead,
} from '../components/PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { ROUTES, href, open } from '../routes.js';
import {
  archiveOrganisation, createOrganisation, getOrganisation, getOrganisationTree, researchStamp,
  type OrganisationDetailView, type OrganisationTree,
} from '../api/organisations.js';
import { listMonitors } from '../api/monitors.js';
import { startRun } from '../api/runs.js';
import { ApiError } from '../api/client.js';
import { tCtx } from '../../shared/i18n.js';
import type { CommitmentView, Monitor, Organisation, ProfileResponse } from '../../shared/types.js';
import type { PageProps } from './registry.js';
import './OrganisationPage.de.js';
import './OrganisationPage.fr.js';

/**
 * The tree's two people counts, kept apart because they are two different facts: who is
 * affiliated with THIS row, and who is affiliated anywhere in the tree (distinct persons, so
 * somebody affiliated with both a unit and its parent is one person, not two). Both are the
 * server's own counts — this only chooses which to show where.
 */
function treePeople(tree: OrganisationTree | null, direct: number): { direct: number; total: number; units: number } {
  if (!tree) return { direct, total: direct, units: 0 };
  return {
    direct: typeof tree.root.people === 'number' ? tree.root.people : direct,
    total: typeof tree.people_in_tree === 'number' ? tree.people_in_tree : direct,
    units: typeof tree.units_count === 'number' ? tree.units_count : tree.units.length,
  };
}

/**
 * Two columns round-4 added to `organisations` that the shared row type does not have to grow a
 * page-only shape for: `merged_into` (the survivor of a held merge) and `merged_at`. Read
 * defensively — an older server simply does not send them, and the lines below stay silent.
 */
function mergedFields(o: Organisation | undefined | null): { merged_into: number | null; merged_at: string | null } {
  const row = (o ?? {}) as Partial<Organisation> & { merged_into?: unknown; merged_at?: unknown };
  return {
    merged_into: typeof row.merged_into === 'number' ? row.merged_into : null,
    merged_at: typeof row.merged_at === 'string' ? row.merged_at : null,
  };
}

export default function OrganisationPage({ organisationId }: PageProps) {
  const t = useT();
  const { fmtDate, fmtMoney, fmtNum } = useLocale();
  const toast = useToast();

  const [data, setData] = useState<OrganisationDetailView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [researching, setResearching] = useState(false);
  // null = the monitors service has not answered (unknown); [] = it answered zero (nil).
  const [monitors, setMonitors] = useState<Monitor[] | null>(null);
  const [monitorsError, setMonitorsError] = useState<string | null>(null);
  // Round 3: the profile zone loads the raster and hands it back so the masthead can print the
  // tier without a second request. Null is "nothing has answered", not "no tier".
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  // The survivor this row was merged into (an archived row keeps `merged_into` for good — a
  // merge is not undone from the session ledger). It is a named row, so it is fetched for its
  // ref and its name; a fetch that fails leaves the line naming the id rather than inventing
  // a name. The parent chain above it is the breadcrumb, below.
  const [mergedInto, setMergedInto] = useState<{ ref: string; name: string } | null>(null);
  /** The breadcrumb when the TREE could not be read: this row's parent chain, walked one named
   *  row at a time. Empty while nothing has answered — never a guessed name. */
  const [walked, setWalked] = useState<Array<{ id: number; ref: string; name: string }>>([]);
  // The units under this organisation. null = the tree has not answered (unknown); a tree with
  // an empty `units` is a counted zero, and the two never render the same.
  const [tree, setTree] = useState<OrganisationTree | null>(null);
  const [treeError, setTreeError] = useState<string | null>(null);
  // "New unit" — the same new-organisation write the board makes, with parent_id preset.
  const [unitOpen, setUnitOpen] = useState(false);
  const [unitName, setUnitName] = useState('');
  const [creatingUnit, setCreatingUnit] = useState(false);

  const load = useCallback(async () => {
    if (!organisationId) return;
    setLoading(true);
    try {
      const res = await getOrganisation(organisationId);
      setData(res);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [organisationId]);

  useEffect(() => { void load(); }, [load]);

  const loadMonitors = useCallback(async () => {
    if (!organisationId) return;
    try {
      const res = await listMonitors({ subject_kind: 'organisation', subject_id: organisationId });
      setMonitors(res.items);
      setMonitorsError(null);
    } catch (err) {
      // Silence, not zero: the cell keeps "not collected" and says the service did not answer.
      setMonitorsError(err instanceof Error ? err.message : String(err));
    }
  }, [organisationId]);

  useEffect(() => { void loadMonitors(); }, [loadMonitors]);

  const mergedIntoId = mergedFields(data?.organisation).merged_into ?? null;
  const parentId = data?.organisation.parent_id ?? null;

  /** The one sentence both retired verbs give as their reason: it names the survivor when the
   *  survivor's ref has been read, and falls back to the plain fact when it has not. */
  const mergedVerbReason = mergedInto
    ? t('This row was merged into {ref}. Run it on the survivor.', { ref: `${mergedInto.ref} ${mergedInto.name}` })
    : t('This row was merged away. Run it on the survivor.');

  useEffect(() => {
    let live = true;
    setMergedInto(null);
    if (!mergedIntoId) return;
    void getOrganisation(mergedIntoId).then(
      (res) => { if (live) setMergedInto({ ref: res.organisation.ref, name: res.organisation.name }); },
      () => { if (live) setMergedInto(null); },
    );
    return () => { live = false; };
  }, [mergedIntoId]);

  // THE FALLBACK BREADCRUMB. `GET /organisations/:id/tree` carries the ancestors, so the walk
  // below runs ONLY when that call failed — one named row at a time, to the tree's own depth
  // limit (§2b: depth ≤ 3). A hop that fails ends the walk; what was read still renders, and
  // the "part of #id" line covers the case where nothing was.
  useEffect(() => {
    let live = true;
    setWalked([]);
    if (!parentId || treeError === null) return;
    void (async () => {
      const chain: Array<{ id: number; ref: string; name: string }> = [];
      let next: number | null = parentId;
      for (let hop = 0; next !== null && hop < 3; hop += 1) {
        try {
          const res: OrganisationDetailView = await getOrganisation(next);
          chain.unshift({ id: res.organisation.id, ref: res.organisation.ref, name: res.organisation.name });
          next = res.organisation.parent_id ?? null;
        } catch {
          break;
        }
      }
      if (live) setWalked(chain);
    })();
    return () => { live = false; };
  }, [parentId, treeError]);

  const loadTree = useCallback(async () => {
    if (!organisationId) return;
    try {
      const res = await getOrganisationTree(organisationId);
      setTree(res);
      setTreeError(null);
    } catch (err) {
      // Silence, not zero: the Units zone says the tree did not answer.
      setTree(null);
      setTreeError(err instanceof Error ? err.message : String(err));
    }
  }, [organisationId]);

  useEffect(() => { void loadTree(); }, [loadTree]);

  /**
   * "Research" (BUILD-BRIEF-R4 §4): ONE verb, one run. `deep_research` reads the record, finds
   * the website if the row has none, searches and reads the web through the scout, and files
   * what it can cite — raster cells, people, a domain — as PROPOSALS. Nothing lands on the
   * record here, which is why the toast points at Review and not at a result.
   *
   * THE VERB THEN OPENS THE RUN'S DESK (R3f §2): a run the operator started is a run the
   * operator can watch — its steps as they arrive, its ledger and what it filed. A server that
   * answered without an id leaves the desk out and the toast standing.
   */
  const research = useCallback(async () => {
    if (!organisationId) return;
    setResearching(true);
    try {
      const run = await startRun({ skill: 'deep_research', subject_kind: 'organisation', subject_id: organisationId });
      toast('ok', t('Research {ref} queued. Everything it can cite arrives in Review as a proposal — cells, people, a website — and nothing is written until you hold it.', { ref: run.ref }));
      if (run.id) open(ROUTES.run({ id: run.id }));
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not queue the research run.'));
    } finally {
      setResearching(false);
    }
  }, [organisationId, toast, t]);

  /** "New unit": the same write the Organisations board's New organisation makes, with this
   *  organisation preset as the parent. The operator is its author, so it is written, not
   *  proposed — exactly as a hand-typed organisation always was. */
  const submitUnit = useCallback(async () => {
    const name = unitName.trim();
    if (!name || !organisationId) return;
    setCreatingUnit(true);
    try {
      const unit = await createOrganisation({ name, parent_id: organisationId });
      setUnitOpen(false);
      setUnitName('');
      toast('ok', t('{name} created as a unit of this organisation.', { name: unit.name }));
      open(ROUTES.org({ id: unit.id }));
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not create the unit.'));
    } finally {
      setCreatingUnit(false);
    }
  }, [unitName, organisationId, toast, t]);

  const doArchive = useCallback(async () => {
    if (!organisationId) return;
    setArchiving(true);
    try {
      await archiveOrganisation(organisationId);
      setArchiveOpen(false);
      toast('ok', t('Organisation archived.'));
      void load();
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not archive the organisation.'));
    } finally {
      setArchiving(false);
    }
  }, [organisationId, toast, t, load]);

  if (!organisationId) {
    return (
      <PageShell>
        <EmptyState kind="error" title={t('No organisation addressed')} detail={t('The link did not name an organisation.')} />
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <a className="back" onClick={() => open(ROUTES.orgs())}>{`← ${t('Organisations')}`}</a>
        <EmptyState kind="error" title={t('Could not load')} detail={error} onRetry={load} />
      </PageShell>
    );
  }

  if (loading || !data) {
    return (
      <PageShell>
        <a className="back" onClick={() => open(ROUTES.orgs())}>{`← ${t('Organisations')}`}</a>
        <Mast title={t('Loading…')} />
      </PageShell>
    );
  }

  const { organisation: o, people, opportunities, commitments, next_move, last_touch, days_since_touch } = data;

  const ballLine = ballFor(commitments);
  const peopleNames = people.slice(0, 3).map((p) => p.name);
  // The whole detail, not the row: the date is a column ON the organisation, the tally beside
  // it belongs to the RUN (`last_research.proposed`) and is shipped next to the row.
  const lastResearch = researchStamp(data);
  const units = tree?.units ?? [];
  const counted = treePeople(tree, people.length);
  // The tree carries the parent chain; the walk above is only what stands in when it did not
  // answer. Either way the breadcrumb is made of NAMED rows, never of ids.
  const ancestors = tree?.root.ancestors?.length ? tree.root.ancestors : walked;
  /** The best tier among the people here — the contact cadence, not the market tier. */
  const bestTier = people.some((p) => p.tier === 'A') ? 'A' : people.some((p) => p.tier === 'B') ? 'B' : people.length ? 'C' : null;
  /** The round-3 market tier: what the ICP rules scored this organisation. Absent until scored. */
  const orgTier = profile?.score ?? null;
  const activeMonitors = monitors ? monitors.filter((m) => m.status === 'active') : null;
  const monitorKinds = monitors ? [...new Set(monitors.map((m) => m.kind))] : [];
  const lastMonitorRun = monitors
    ? monitors.map((m) => m.last_run_at).filter((d): d is string => !!d).sort().at(-1) ?? null
    : null;

  return (
    <PageShell>
      <a className="back" onClick={() => open(ROUTES.orgs())}>{`← ${t('Organisations')}`}</a>
      <Mast
        kicker={
          <>
            {t('Organisation')} ·{' '}
            {/* the kicker is a .u-label, which uppercases and tracks; a ref is neither */}
            <span style={{ letterSpacing: 0, textTransform: 'none' }}><Ref>{o.ref}</Ref></span>
          </>
        }
        title={/* The dossier names the organisation once, so its mark is read at masthead size
                  — a 28px favicon beside a 30px name was a decoration, not a picture.
                  `.mast-name` keeps the h1 an h1 and lets the mark ride beside the name
                  instead of inside its line box. */
        <span className="mast-name">
          <OrgLogo id={o.id} name={o.name} scale="lg" />
          <span>
            {o.name}
            <span className={`statedot${o.status !== 'active' ? ' statedot--idle' : ''}`} />
            <span className="statelabel">{o.is_client ? t('client') : t(o.status)}</span>
          </span>
        </span>}
        sub={<>
          {/* The breadcrumb (§2b): the whole chain, each name a link, this row last and plain.
              It replaces the bare "part of" link — a unit three levels down was unreadable as
              one parent name. When no ancestor could be read the fallback below still says the
              row has a parent, naming its id rather than inventing a name. */}
          {ancestors.length > 0 ? (
            <>
              <span>
                {ancestors.map((a) => (
                  <Fragment key={a.id}>
                    <Lnk href={href(ROUTES.org({ id: a.id }))} title={a.ref}>{a.name}</Lnk>
                    {' › '}
                  </Fragment>
                ))}
                {o.name}
              </span>
              <span className="sep">·</span>
            </>
          ) : parentId ? (
            <>
              {t('part of')}{' '}
              <Lnk href={href(ROUTES.org({ id: parentId }))}><Ref>{`#${parentId}`}</Ref></Lnk>
              <span className="sep">·</span>
            </>
          ) : null}
          {o.legal_form && <>{o.legal_form}<span className="sep">·</span></>}
          {(o.seat || o.canton) && <>{[o.seat, o.canton].filter(Boolean).join(' ')}<span className="sep">·</span></>}
          {o.uid && <><Ref>{o.uid}</Ref><span className="sep">·</span></>}
          {o.domains?.[0] && <><Ref>{o.domains[0]}</Ref><span className="sep">·</span></>}
          {o.sector ?? t('sector not on record')}
          {/* the tier hook: the round-3 market tier, with the contact tier behind it */}
          <span className="sep">·</span>
          <span data-slot="tier">
            {orgTier ? (
              <Tag
                tone={TIER_TONE[orgTier.tier]}
                title={t('fit {f} × timing {ti} × access {a} = {c} — the score below says which rules fired', {
                  f: orgTier.fit, ti: orgTier.timing, a: orgTier.access, c: orgTier.composite,
                })}
              >
                {t(orgTier.tier)} <Fig>{orgTier.composite}</Fig>
              </Tag>
            ) : profile ? (
              // The profile answered and carries no score: nobody has scored it.
              <span className="fv--unknown" title={t('the ICP rules have not scored this organisation')}>{t('not scored')}</span>
            ) : (
              // The profile has not answered at all — which is not the same as unscored.
              <span className="fv--unknown">{t('not collected')}</span>
            )}
            {bestTier && <> <Tag tone="ghost" title={t('best tier among the people here')}>{t('tier')} {bestTier}</Tag></>}
          </span>
          {/* A merged row says so before anything else on it is read: what stands here is
              history, and the live record is the survivor. */}
          {mergedIntoId && (
            <>
              <span className="sep">·</span>
              <span className="fv--attend" title={t('this row was archived by a held dedupe proposal; it keeps merged_into for good')}>
                {t('merged into')}{' '}
                <Lnk href={href(ROUTES.org({ id: mergedIntoId }))}>
                  <Ref>{mergedInto?.ref ?? `#${mergedIntoId}`}</Ref>
                </Lnk>
                {mergedInto ? <> {mergedInto.name}</> : null}
                {/* `organisations` records no merge timestamp of its own, so the date shown is
                    the row's last change — said in the title, not passed off as the merge. */}
                {mergedFields(o).merged_at ? (
                  <> {t('on')} <Fig>{fmtDate(mergedFields(o).merged_at)}</Fig></>
                ) : o.updated_at ? (
                  <> {t('on')} <Fig title={t('the merge date is not recorded separately; this is the row’s last change')}>{fmtDate(o.updated_at)}</Fig></>
                ) : null}
              </span>
            </>
          )}
          {/* LAST RESEARCH (R4 §4): the run that last worked this organisation, the day it ended
              and what it filed. The ref is an identity and a door — it opens that run's desk,
              which is the account now. The tally beside it is printed only when the server
              counted one: a run that proposed nothing and a run nobody counted are different
              facts, and neither is written as "0 proposed". */}
          {(lastResearch.run || lastResearch.last_researched_at) && (
            <>
              <span className="sep">·</span>
              <span title={lastResearch.run
                ? t('{ref} read the web for this organisation and filed what it could cite', { ref: lastResearch.run.run_ref })
                : t('the last time a research run ended on this organisation')}>
                {t('last research')}
                {lastResearch.run && (
                  <> <Lnk href={href(ROUTES.run({ id: lastResearch.run.run_id }))}><Ref>{lastResearch.run.run_ref}</Ref></Lnk></>
                )}
                {lastResearch.last_researched_at
                  ? <> <span className="sep">·</span> <Fig>{fmtDate(lastResearch.last_researched_at)}</Fig></>
                  : <> <span className="sep">·</span> <span className="fv--unknown">{t('still running')}</span></>}
                {lastResearch.proposed !== null && (
                  <> <span className="sep">·</span>{' '}
                    <Fig className={lastResearch.proposed === 0 ? 'fv--nil' : undefined}>{fmtNum(lastResearch.proposed)}</Fig>
                    {' '}{t('proposed')}
                  </>
                )}
              </span>
            </>
          )}
        </>}
        verbs={<>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setArchiveOpen(true)} disabled={o.status === 'archived'}>
            {t('Archive')}
          </button>
          {/* THE ONE ACCENT VERB (R4 §4). A merged-away row is history: spending a run on it
              would read the web for a name the record has retired, and every proposal it filed
              would land on the archived subject. The survivor is where that work belongs. */}
          <button
            type="button"
            className="btn btn-accent"
            disabled={researching || mergedIntoId !== null}
            title={mergedIntoId !== null ? mergedVerbReason : t('Send an agent to read the web for this organisation: the raster cells it can cite, the people it meets, the website if the row has none. Everything arrives in Review as a proposal.')}
            onClick={() => { void research(); }}
          >
            {researching ? t('Queuing…') : tCtx(t, 'Research', 'verb')}
          </button>
        </>}
      />

      <Rail>
        {/* PEOPLE reads the WHOLE TREE when this organisation has units, because "how many
            people do we know at OSEO Vaud" is a question about OSEO Vaud and everything under
            it. The direct count stays in the detail line, so the two are never confused. A
            tree that has not answered leaves the cell exactly as round 1 drew it. */}
        <RailCell
          label={t('People')}
          value={fmtNum(counted.units ? counted.total : people.length)}
          unit={counted.units ? t('in {n} units', { n: fmtNum(counted.units) }) : undefined}
          detail={counted.units
            ? `${t('{n} direct', { n: fmtNum(counted.direct) })} · ${peopleNames.length ? peopleNames.join(' · ') : t('nobody on record here')}`
            : peopleNames.length ? peopleNames.join(' · ') : t('nobody on record here')}
        />
        <RailCell
          label={t('Day')}
          value={days_since_touch !== null ? fmtNum(days_since_touch) : '—'}
          unit={days_since_touch !== null ? t('since last touch') : undefined}
          tone={days_since_touch === null ? 'nil' : undefined}
          detail={last_touch ? `${t(last_touch.kind)} · ${fmtDate(last_touch.occurred_on)} · ${last_touch.title}` : t('no interaction on record')}
        />
        <RailCell
          label={t('Ball')}
          value={ballLine ? t(ballLine.owner) : '—'}
          unit={ballLine?.lateDays ? `${ballLine.lateDays}d ${t('late')}` : undefined}
          tone={ballLine?.lateDays ? 'broken' : ballLine ? 'attend' : 'nil'}
          detail={ballLine ? ballLine.text : t('no open commitment')}
        />
        <RailCell
          label={t('Deals')}
          value={fmtNum(opportunities.length)}
          unit={t('open')}
          tone={opportunities.length ? undefined : 'nil'}
          detail={opportunities.length
            ? `${fmtMoney(opportunities.reduce((sum, op) => sum + Number(op.face_value ?? 0), 0))} ${t('face value')}`
            : t('no open deal')}
        />
        <RailCell
          label={t('Monitors')}
          value={activeMonitors === null ? t('not collected') : fmtNum(activeMonitors.length)}
          unit={activeMonitors && activeMonitors.length ? t('active') : undefined}
          tone={activeMonitors === null ? 'unknown' : activeMonitors.length ? undefined : 'nil'}
          detail={activeMonitors === null
            ? (monitorsError ? t('the monitors service did not answer') : t('counting the monitors…'))
            : monitors && monitors.length
              ? `${monitorKinds.map((k) => t(k)).join(' · ')}${lastMonitorRun ? ` · ${t('last run')} ${fmtDate(lastMonitorRun)}` : ` · ${t('never run')}`}`
              : t('no monitor watches this organisation')}
        />
      </Rail>

      <Zone>
        <ZoneHead label={t('Next move')} />
        {next_move ? (
          <Sheet edge pad>
            <div className="move-head">
              <h2>{next_move.headline}</h2>
            </div>
            <p className="read" style={{ margin: '8px 0 0' }}>{next_move.detail}</p>
          </Sheet>
        ) : (
          <Sheet pad>
            <p className="zone-empty">{t('Nothing owed and nothing stale here — the record is quiet.')}</p>
          </Sheet>
        )}
      </Zone>

      <Zone>
        <ZoneHead label={t('People here')} count={fmtNum(people.length)} />
        {people.length === 0 ? (
          <p className="zone-empty">{t('No person is affiliated with this organisation on the record.')}</p>
        ) : (
          <Sheet clip>
            <Tbl>
              <thead>
                <tr>
                  <th>{t('Person')}</th>
                  <th>{t('Role')}</th>
                  <th>{t('Tier')}</th>
                  <th>{t('Status')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <span className="idcell">
                        <PersonAvatar id={p.id} name={p.name} />
                        <span>
                          <span className="w">{p.name}</span> <Ref>{p.ref}</Ref>
                          {p.affiliation_status === 'proposed' && <> <Att kind="proposed" /></>}
                        </span>
                      </span>
                    </td>
                    <td>{p.role_title ?? <span className="n-none">—</span>}</td>
                    <td>{t(p.tier)} · <Fig unit="d">{p.cadence_days}</Fig></td>
                    <td>{p.affiliation_status === 'held' ? <Att kind="held" /> : <Tag tone="ghost">{t('proposed')}</Tag>}</td>
                    <td><button type="button" className="lnk" onClick={() => open(ROUTES.person({ id: p.id }))}>{t('Open →')}</button></td>
                  </tr>
                ))}
              </tbody>
            </Tbl>
          </Sheet>
        )}
      </Zone>

      {/* ── the units (§2b) ─────────────────────────────────────────────────
          `organisations.parent_id` given a face: the departments, sites and programmes under
          this row, indented by depth. Every figure is the server's count over that unit alone,
          so the tree total in the rail and these rows add up. */}
      <Zone id="units">
        <ZoneHead
          label={t('Units')}
          count={tree ? fmtNum(units.length) : undefined}
          note={t('departments, sites and programmes under this organisation')}
          route={
            <button type="button" className="zone-route" onClick={() => open(ROUTES.people({ tree_of: o.id }))}>
              {t('All people in this tree')} →
            </button>
          }
        >
          <button type="button" className="zone-route" onClick={() => setUnitOpen(true)}>{t('New unit')}</button>
        </ZoneHead>
        {tree === null ? (
          <p className="zone-empty">
            {treeError ? t('The tree could not be read: {error}', { error: treeError }) : t('Loading…')}
          </p>
        ) : units.length === 0 ? (
          <p className="zone-empty">
            {t('No unit sits under this organisation. A unit is a department, a site or a programme with its own name — added here, or proposed by a research run that read it on an organigramme.')}
          </p>
        ) : (
          <Sheet clip>
            <Tbl>
              <thead>
                <tr>
                  <th>{t('Unit')}</th>
                  <th className="num">{t('People')}</th>
                  <th className="num">{t('Open deals')}</th>
                  <th className="num">{t('Last contact')}</th>
                </tr>
              </thead>
              <tbody>
                {units.map((u) => (
                  <tr key={u.id}>
                    {/* depth is drawn, not written: one indent per level under this row */}
                    {/* a unit is an organisation, so it carries an organisation's mark */}
                    <td style={{ paddingLeft: 8 + (Math.max(1, u.depth) - 1) * 18 }}>
                      <span className="idcell">
                        <OrgLogo id={u.id} name={u.name} />
                        <span>
                          <Lnk href={href(ROUTES.org({ id: u.id }))}>{u.name}</Lnk> <Ref>{u.ref}</Ref>
                        </span>
                      </span>
                    </td>
                    <td className="num"><Fig className={u.people === 0 ? 'fv--nil' : undefined}>{fmtNum(u.people)}</Fig></td>
                    <td className="num"><Fig className={u.open_deals === 0 ? 'fv--nil' : undefined}>{fmtNum(u.open_deals)}</Fig></td>
                    <td className="num">
                      {u.last_contact_on
                        ? <Fig>{fmtDate(u.last_contact_on)}</Fig>
                        : <span className="n-none">{t('no interaction on record')}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tbl>
          </Sheet>
        )}
      </Zone>

      <Zone>
        <ZoneHead label={t('Deals')} count={fmtNum(opportunities.length)} route={
          <button type="button" className="zone-route" onClick={() => open(ROUTES.pipeline())}>{t('Pipeline →')}</button>
        } />
        {opportunities.length === 0 ? (
          <p className="zone-empty">{t('No opportunity is open for this organisation.')}</p>
        ) : (
          <Sheet clip>
            {opportunities.map((op) => (
              <div className="row" key={op.id} style={{ gridTemplateColumns: '1fr 130px 120px' }}>
                <span>
                  <Ref>{op.ref}</Ref> <span className="w">{op.title}</span>
                  <br />
                  <span className="d">
                    {t(op.stage)} · <Fig unit={` ${t('in stage')}`}>{op.days_in_stage}</Fig>
                    {op.next_step && <> · {op.next_step}{op.next_step_due ? ` ${fmtDate(op.next_step_due)}` : ''}</>}
                  </span>
                </span>
                <Fig>{fmtMoney(op.face_value)}</Fig>
                <Tag tone={op.stale ? 'attend' : 'neutral'}>{op.stale ? t('stale') : t(op.stage)}</Tag>
              </div>
            ))}
          </Sheet>
        )}
      </Zone>

      <Zone>
        <ZoneHead label={t('Open commitments')} count={fmtNum(commitments.length)} />
        {commitments.length === 0 ? (
          <p className="zone-empty">{t('No commitment is open on either side for this organisation.')}</p>
        ) : (
          <Sheet clip>
            {commitments.map((c) => {
              const late = c.due_on && c.due_on < new Date().toISOString().slice(0, 10);
              return (
                <div className="row" key={c.id} style={{ gridTemplateColumns: '90px 1fr 120px' }}>
                  <span className="k">{t(c.owner)}</span>
                  <span>
                    {c.text}
                    {c.person_name && <span className="d"> · {c.person_name}</span>}
                    {c.source_ref && <span className="d"> · <Ref>{c.source_ref}</Ref></span>}
                  </span>
                  <span>
                    {c.due_on ? <Fig>{fmtDate(c.due_on)}</Fig> : <span className="n-none">{t('undated')}</span>}
                    {late && <> <Tag tone="broken">{t('late')}</Tag></>}
                  </span>
                </div>
              );
            })}
          </Sheet>
        )}
      </Zone>

      <NetworkZone organisationId={o.id} />

      <SourceMentionsZone organisationId={o.id} />

      <ProfileZone organisationId={o.id} onProfile={setProfile} />

      {/* Below the profile, because a document is what the profile's cells rest on. */}
      <DocumentsZone profile={profile} />

      <DossierZone subjectKind="organisation" subjectId={o.id} />

      <SignalsZone subjectKind="organisation" subjectId={o.id} />

      <Zone>
        <ZoneHead
          label={t('Monitors')}
          count={monitors ? fmtNum(monitors.length) : undefined}
          note={t('a target with a cadence; every run records its outcome')}
          route={<button type="button" className="zone-route" onClick={() => open(ROUTES.signals())}>{t('Signals')} →</button>}
        />
        {monitors === null ? (
          <p className="zone-empty">{monitorsError ? t('The monitors could not be read: {error}', { error: monitorsError }) : t('Loading…')}</p>
        ) : monitors.length === 0 ? (
          <p className="zone-empty">
            {t('No monitor watches this organisation. Add one on the Signals page — its website, its careers page, its SHAB publications by UID, news mentions — and its runs and signals appear here.')}
          </p>
        ) : (
          <Sheet clip>
            <Tbl>
              <thead>
                <tr>
                  <th>{t('Kind')}</th>
                  <th>{t('Target')}</th>
                  <th className="num">{t('Cadence')}</th>
                  <th className="num">{t('Last run')}</th>
                  <th>{t('Outcome')}</th>
                  <th>{t('Status')}</th>
                </tr>
              </thead>
              <tbody>
                {monitors.map((m) => (
                  <tr key={m.id}>
                    <td>{t(m.kind)}</td>
                    <td>{m.target ? <Ref>{m.target}</Ref> : <span className="n-none">—</span>}</td>
                    <td className="num"><Fig unit="h">{m.cadence_hours}</Fig></td>
                    <td className="num">{m.last_run_at ? <Fig>{fmtDate(m.last_run_at)}</Fig> : <span className="n-none">{t('never run')}</span>}</td>
                    <td>{m.last_outcome ? <Tag tone={m.last_outcome === 'signal' ? 'attend' : m.last_outcome === 'error' || m.last_outcome === 'refused' ? 'broken' : 'neutral'}>{t(m.last_outcome)}</Tag> : <span className="n-none">—</span>}</td>
                    <td><Tag tone={m.status === 'active' ? 'ok' : m.status === 'failed' ? 'broken' : 'ghost'}>{t(m.status)}</Tag></td>
                  </tr>
                ))}
              </tbody>
            </Tbl>
          </Sheet>
        )}
      </Zone>

      {/* The new-organisation form, with this organisation preset as the parent. It is the same
          write as the board's New organisation — one `organisations` row, authored by the
          operator — and the preset is the only difference. */}
      <ConfirmModal
        open={unitOpen}
        title={t('New unit')}
        confirmLabel={creatingUnit ? t('Saving…') : t('Save')}
        onConfirm={() => { void submitUnit(); }}
        onCancel={() => { setUnitOpen(false); setUnitName(''); }}
      >
        <div className="form-group">
          <label className="form-label" htmlFor="unit-new-name">{t('Name')}</label>
          <input
            id="unit-new-name"
            className="form-input"
            autoFocus
            value={unitName}
            onChange={(e) => setUnitName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submitUnit(); }}
          />
        </div>
        <p className="zone-empty" style={{ marginTop: 12 }}>
          {t('The unit is written as an organisation whose parent is {name}. It appears in the Units zone and in this tree’s people.', { name: o.name })}
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={archiveOpen}
        title={t('Archive this organisation?')}
        confirmLabel={archiving ? t('Saving…') : t('Archive')}
        danger
        onConfirm={doArchive}
        onCancel={() => setArchiveOpen(false)}
      >
        <p>{t('The organisation stays on the record; it drops out of the active board.')}</p>
      </ConfirmModal>
    </PageShell>
  );
}

function ballFor(commitments: CommitmentView[]): { owner: 'us' | 'them'; lateDays: number | null; text: string } | null {
  if (!commitments.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  const overdue = commitments.find((c) => c.due_on && c.due_on < today);
  const pick = overdue ?? commitments[0];
  const lateDays = pick.due_on && pick.due_on < today
    ? Math.round((Date.parse(today) - Date.parse(pick.due_on)) / 86_400_000)
    : null;
  return { owner: pick.owner, lateDays, text: pick.text };
}
